import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, ExternalLink, Pause, Play, Wand2 } from 'lucide-react';
import { formatCurrency } from '@tally/shared';
import { accountsApi } from '@/services/api';
import { cn } from '@/utils/cn';

const STATE_LABEL: Record<string, { label: string; tone: string; help: string }> = {
  sampling: { label: 'Sampling', tone: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300', help: 'Reading the newest emails first, then pausing so you can check them.' },
  awaiting_review: { label: 'Waiting for your review', tone: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300', help: 'Check the emails below. If they look right, continue the scan.' },
  running: { label: 'Running', tone: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300', help: 'Reading your full email history, newest first. It resumes automatically after AI quota pauses.' },
  paused: { label: 'Paused', tone: 'bg-muted text-muted-foreground', help: 'Nothing is being read. Start or continue whenever you are ready.' },
};

/** Scan status, controls, and the latest extractions so parsing quality can be checked before scanning everything. */
export function ScanActivity({ accountId, label }: { accountId: string; label?: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(true);
  const { data, isLoading } = useQuery({
    queryKey: ['scan', accountId],
    queryFn: () => accountsApi.scan(accountId),
    refetchInterval: 15_000,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['scan', accountId] });
  const pause = useMutation({ mutationFn: () => accountsApi.pauseScan(accountId), onSuccess: refresh });
  const resume = useMutation({ mutationFn: (body: { mode: 'all' | 'sample'; count?: number }) => accountsApi.resumeScan(accountId, body), onSuccess: refresh });

  if (isLoading || !data) return null;
  const scan = data.data;
  const state = STATE_LABEL[scan.state] ?? STATE_LABEL.paused;
  const s = scan.stages;
  const read = s.extracted + s.failed;
  const lastJobError = scan.jobs.find((j) => j.lastError)?.lastError;
  const run = scan.lastRun;

  return (
    <div className="mt-2 rounded-lg border border-border bg-muted/20 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm font-medium">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Scan activity{label ? ` · ${label}` : ''}
          <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', state.tone)}>{state.label}</span>
        </button>
        <div className="flex flex-wrap gap-2">
          {scan.state === 'running' || scan.state === 'sampling' ? (
            <button onClick={() => pause.mutate()} disabled={pause.isPending} className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent">
              <Pause className="h-3.5 w-3.5" /> Pause scan
            </button>
          ) : (
            read === 0 ? (
              <button onClick={() => resume.mutate({ mode: 'sample', count: 50 })} disabled={resume.isPending} className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90">
                <Play className="h-3.5 w-3.5" /> Start scan: read newest 50, then pause
              </button>
            ) : (
            <>
              <button onClick={() => resume.mutate({ mode: 'sample', count: 50 })} disabled={resume.isPending} className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent">
                <Wand2 className="h-3.5 w-3.5" /> Read 50 more, then pause
              </button>
              <button onClick={() => resume.mutate({ mode: 'all' })} disabled={resume.isPending} className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90">
                <Play className="h-3.5 w-3.5" /> Continue full scan
              </button>
            </>
            )
          )}
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{state.help}</p>

      {open && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-5">
            <Stat label="Waiting to check" value={s.listed} />
            <Stat label="Likely purchases" value={s.candidate} />
            <Stat label="Skipped (not purchases)" value={s.skipped} />
            <Stat label="Read by AI" value={s.extracted} />
            <Stat label="Could not read" value={s.failed} warn={s.failed > 0} />
          </div>

          {run && (
            <p className="text-xs text-muted-foreground">
              Last run {new Date(run.finishedAt).toLocaleString()} · {Math.round(run.durationMs / 1000)}s · read {run.extracted} ·
              {' '}{(run.usage?.inputTokens ?? 0).toLocaleString()} AI tokens in / {(run.usage?.outputTokens ?? 0).toLocaleString()} out
              {run.quotaExhausted && ' · paused for the daily AI quota (resumes automatically)'}
              {run.llmUnavailable && ' · AI not configured'}
            </p>
          )}
          {lastJobError && (
            <p className="flex items-start gap-1.5 text-xs text-destructive">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Last error: {lastJobError}
            </p>
          )}

          {scan.recent.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs font-medium">Latest emails read ({read} so far) — check that dates, amounts and references are right</p>
              <div className="max-h-96 overflow-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-card text-left text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1.5">Email</th>
                      <th className="px-2">Type</th>
                      <th className="px-2">Vendor</th>
                      <th className="px-2">Amount</th>
                      <th className="px-2">Service dates</th>
                      <th className="px-2">References</th>
                      <th className="px-2">Check</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scan.recent.map((r) => {
                      const e = r.extraction;
                      return (
                        <tr key={r.messageId} className="border-t border-border align-top">
                          <td className="max-w-[200px] px-2 py-1.5">
                            <a href={r.deepLink} target="_blank" rel="noopener noreferrer" className="flex items-start gap-1 text-primary hover:underline">
                              <span className="truncate">{r.subject || r.fromDomain || r.messageId}</span>
                              <ExternalLink className="mt-0.5 h-3 w-3 shrink-0" />
                            </a>
                            <span className="text-muted-foreground">{r.emailDate?.slice(0, 10)}</span>
                          </td>
                          <td className="px-2 py-1.5">{e.kind?.replace(/_/g, ' ')}</td>
                          <td className="px-2 py-1.5">{e.vendor}{e.via ? <span className="text-muted-foreground"> via {e.via}</span> : null}</td>
                          <td className="whitespace-nowrap px-2 py-1.5">
                            {e.money ? formatCurrency(e.money.amount, e.money.currency) : '—'}
                            {e.money && !e.money.charged && <span className="block text-muted-foreground">not charged</span>}
                            {e.money?.isRefund && <span className="block text-green-600">refund</span>}
                          </td>
                          <td className="whitespace-nowrap px-2 py-1.5">
                            {e.serviceStart ? `${e.serviceStart}${e.serviceStartTime ? ` ${e.serviceStartTime}` : ''}` : '—'}
                            {e.serviceEnd && e.serviceEnd !== e.serviceStart && <span className="block">→ {e.serviceEnd}{e.serviceEndTime ? ` ${e.serviceEndTime}` : ''}</span>}
                            {e.segments?.length > 0 && <span className="block text-muted-foreground">{e.segments.map((sg: any) => `${sg.fromIata ?? sg.fromCity}→${sg.toIata ?? sg.toCity}`).join(', ')}</span>}
                          </td>
                          <td className="px-2 py-1.5 font-mono">{(e.confirmationNumbers ?? []).join(', ') || '—'}</td>
                          <td className="px-2 py-1.5">
                            {e.issues?.length ? (
                              <span className="flex items-center gap-1 text-amber-600" title={e.issues.join(', ')}><AlertTriangle className="h-3 w-3" /> {e.issues.join(', ').replace(/_/g, ' ')}</span>
                            ) : e.verified ? (
                              <span className="flex items-center gap-1 text-green-600"><CheckCircle2 className="h-3 w-3" /> self-checked</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                            {e.corrections?.length > 0 && (
                              <span className="block text-muted-foreground" title={e.corrections.join('\n')}>{e.corrections.length} correction{e.corrections.length > 1 ? 's' : ''}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {scan.failures.length > 0 && (
            <div className="text-xs">
              <p className="mb-1 font-medium">Emails that could not be read</p>
              <ul className="space-y-0.5 text-muted-foreground">
                {scan.failures.map((f) => <li key={f.messageId}>{f.subject || f.messageId}: {f.reason} ({f.attempts} attempts)</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className="rounded-md border border-border bg-card px-2 py-1.5">
      <p className={cn('text-sm font-semibold', warn && 'text-destructive')}>{value.toLocaleString()}</p>
      <p className="text-muted-foreground">{label}</p>
    </div>
  );
}
